/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { trackWithExtra } from '../../lib/tracking'
import { fetchPosts } from '../api'
import { colors } from '../colors'
import {
  BackButton,
  FilterBar,
  InfinitePostList,
  LoginModal,
  PageContainer,
  PageHeader,
} from '../components'
import type { BargainUserInfo, Chain, PaginationParams, Post } from '../types'

interface CampaignBanner {
  image_url: string
  link_url: string
  tracking_key: string
  requires_login: boolean
}

interface PostsProps {
  bargain_user_info: BargainUserInfo
  posts: Post[]
  random_seed: number
  followed_chains?: Chain[]
  campaign_banners?: CampaignBanner[]
}

const styles: Record<string, React.CSSProperties> = {
  chainHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '28px 0 32px',
  },
  chainLogoContainer: {
    width: '72px',
    height: '72px',
    borderRadius: '36px',
    border: '1px solid #00000018',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    boxSizing: 'border-box',
  },
  chainLogo: {
    maxWidth: '46px',
    maxHeight: '46px',
  },
  chainTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: colors['text-default'],
    textAlign: 'center',
    margin: '8px 12px 0',
  },
  postList: {
    paddingBottom: '100px', // フッターの高さ分の余白を確保
  },
  postListWithPadding: {
    paddingTop: '8px',
    paddingBottom: '100px', // フッターの高さ分の余白を確保
  },
  footerButtons: {
    boxSizing: 'border-box',
    position: 'fixed',
    bottom: '16px',
    left: 0,
    right: 0,
    maxWidth: '640px',
    margin: '0 auto',
    padding: '8px 14px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  filterLoadingOverlay: {
    position: 'relative',
    minHeight: '100vh',
  },
  filterLoadingSpinner: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: '20px',
    borderRadius: '8px',
    zIndex: 1,
    fontSize: '14px',
    color: colors['text-secondary'],
    whiteSpace: 'nowrap',
    minWidth: 'max-content',
  },
  campaignBannerCarousel: {
    overflow: 'hidden',
    paddingTop: '8px',
  },
  campaignBannerCarouselContainer: {
    display: 'flex',
  },
  campaignBannerCarouselSingle: {
    paddingTop: '8px',
    display: 'flex',
    justifyContent: 'center',
  },
  campaignBannerImage: {
    width: '100%',
    height: '100%',
    borderRadius: '9px',
    objectFit: 'cover',
    display: 'block',
    border: `1px solid ${colors['border-light']}`,
  },
  carouselIndicators: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    padding: '8px 0',
  },
  carouselDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#D9D9D9',
  },
  carouselDotActive: {
    backgroundColor: colors['keijiban-coral'],
  },
}

const CampaignBannerItem: React.FC<{
  banner: CampaignBanner
  index: number
  userType: BargainUserInfo['type']
  onLoginRequired: () => void
}> = ({ banner, index, userType, onLoginRequired }) => {
  const ref = useRef<HTMLAnchorElement>(null)
  const isGuest = userType === 'guest'

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          void trackWithExtra(`bargain.message_board.top.carousel.${banner.tracking_key}.imp`, {
            index,
            user_type: userType,
          })
        }
      },
      { threshold: 0.5 }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [index, banner.tracking_key, userType])

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    await trackWithExtra(`bargain.message_board.top.carousel.${banner.tracking_key}.click`, {
      index,
      user_type: userType,
    })
    if (isGuest && banner.requires_login) {
      onLoginRequired()
    } else {
      window.location.href = banner.link_url
    }
  }

  return (
    <a
      ref={ref}
      href={banner.link_url}
      onClick={handleClick}
      style={{ display: 'block', height: '100%' }}
    >
      <img src={banner.image_url} alt="バナー画像" style={styles.campaignBannerImage} />
    </a>
  )
}

const campaignBannerSingleItemStyle = css`
  aspect-ratio: 7 / 2;
  width: calc(100% - 34px);

  @media (min-width: 640px) {
    width: calc(100% - 140px);
  }
`

const campaignBannerItemStyle = css`
  aspect-ratio: 7 / 2;
  min-width: 0;
  margin-right: 8px;
  flex: 0 0 calc(100% - 34px);

  @media (min-width: 640px) {
    flex: 0 0 calc(100% - 140px);
  }
`

const Posts: React.FC<PostsProps> = ({
  posts: initialPosts,
  bargain_user_info,
  random_seed,
  followed_chains = [],
  campaign_banners = [],
}) => {
  // URLパラメータからフィルタータイプを取得
  const urlParams = new URLSearchParams(window.location.search)
  const urlFilterType = urlParams.get('filter_type')
  const urlChainId = urlParams.get('chain_id')

  // 初期フィルタータイプを設定
  const getInitialFilterType = () => {
    if (urlFilterType === 'chain' && urlChainId) {
      return `chain_${urlChainId}`
    }
    return urlFilterType || 'all'
  }

  const getInitialSelectedChain = () => {
    if (urlFilterType === 'chain' && urlChainId) {
      return urlChainId
    }
    return null
  }

  const [filteredPosts, setFilteredPosts] = useState<Post[]>(initialPosts)
  const [isLoadingFilteredPosts, setIsLoadingFilteredPosts] = useState(false)
  const [filterType, setFilterType] = useState<
    'all' | 'following' | 'want_to_eat' | 'themes' | string
  >(getInitialFilterType())
  const [selectedChain, setSelectedChain] = useState<string | null>(getInitialSelectedChain())
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [activeBannerIndex, setActiveBannerIndex] = useState(0)
  const isFirstRender = useRef(true)
  const pageHeaderRef = useRef<HTMLDivElement>(null)
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    containScroll: false,
  })

  const handleFilterTypeChange = (type: 'all' | 'following' | 'want_to_eat' | 'themes') => {
    const url = new URL(window.location.href)

    if (type === 'all') {
      // 「すべて」フィルターが選択された場合、URLのクエリパラメータをクリア
      url.search = ''
    } else {
      // その他のフィルタータイプの場合、URLパラメータを更新
      url.searchParams.set('filter_type', type)
      url.searchParams.delete('chain_id')
    }
    window.history.replaceState({}, '', url.toString())

    setFilterType(type)
    setSelectedChain(null)
  }

  const handleChainFilterChange = (chainId: string) => {
    // URLパラメータを更新
    const url = new URL(window.location.href)
    url.searchParams.set('filter_type', 'chain')
    url.searchParams.set('chain_id', chainId)
    window.history.replaceState({}, '', url.toString())

    setFilterType(`chain_${chainId}`)
    setSelectedChain(chainId)
  }

  // フィルタリングが変更された時に新しいデータを取得
  useEffect(() => {
    const fetchFilteredPosts = async () => {
      if (filterType === 'all' && isFirstRender.current) {
        // 初回レンダー時のみinitialPostsを使用
        setFilteredPosts(initialPosts)
        return
      }

      const params: PaginationParams = {
        page: 1,
        offset: 0,
        random_seed,
      }

      if (filterType !== 'all') {
        if (filterType.startsWith('chain_')) {
          params.filter_type = 'chain'
          params.chain_id = filterType.replace('chain_', '')
        } else {
          params.filter_type = filterType as 'following' | 'want_to_eat' | 'themes'
        }
      }
      // 「すべて」フィルターの場合は、filter_typeとchain_idを設定しない（全ての投稿を取得）

      setIsLoadingFilteredPosts(true)
      try {
        const response = await fetchPosts(params)
        setFilteredPosts(response.posts)
      } catch (error) {
        console.error('Failed to fetch filtered posts:', error)
        setFilteredPosts(initialPosts)
      } finally {
        window.scrollTo({ top: pageHeaderRef.current?.offsetHeight, behavior: 'instant' })
        setIsLoadingFilteredPosts(false)
      }
    }

    void fetchFilteredPosts()
    isFirstRender.current = false
  }, [filterType, random_seed, initialPosts])

  // カルーセルのスライド変更を監視
  useEffect(() => {
    if (!emblaApi) return

    const onSelect = () => {
      setActiveBannerIndex(emblaApi.selectedScrollSnap())
    }

    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi])

  const fetchMorePosts = useCallback(
    async (page: number, offset: number) => {
      const params: PaginationParams = {
        page,
        offset,
        random_seed,
      }

      if (filterType !== 'all') {
        if (filterType.startsWith('chain_')) {
          params.filter_type = 'chain'
          params.chain_id = filterType.replace('chain_', '')
        } else {
          params.filter_type = filterType as 'following' | 'want_to_eat' | 'themes'
        }
      }
      // 「すべて」フィルターの場合は、filter_typeとchain_idを設定しない

      const response = await fetchPosts(params)

      return {
        posts: response.posts,
      }
    },
    [random_seed, filterType]
  )

  return (
    <PageContainer maxWidth="640px">
      <div ref={pageHeaderRef}>
        <PageHeader title="みんなのおいしい口コミ" bargain_user_info={bargain_user_info} />
      </div>

      {campaign_banners.length === 1 && (
        <div style={styles.campaignBannerCarouselSingle}>
          <div css={campaignBannerSingleItemStyle}>
            <CampaignBannerItem
              banner={campaign_banners[0]}
              index={0}
              userType={bargain_user_info.type}
              onLoginRequired={() => setShowLoginModal(true)}
            />
          </div>
        </div>
      )}

      {campaign_banners.length > 1 && (
        <>
          <div ref={emblaRef} style={styles.campaignBannerCarousel}>
            <div style={styles.campaignBannerCarouselContainer}>
              {campaign_banners.map((banner, index) => (
                <div key={banner.tracking_key} css={campaignBannerItemStyle}>
                  <CampaignBannerItem
                    banner={banner}
                    index={index}
                    userType={bargain_user_info.type}
                    onLoginRequired={() => setShowLoginModal(true)}
                  />
                </div>
              ))}
            </div>
          </div>
          <div style={styles.carouselIndicators}>
            {campaign_banners.map((banner, index) => (
              <div
                key={banner.tracking_key}
                style={{
                  ...styles.carouselDot,
                  ...(index === activeBannerIndex ? styles.carouselDotActive : {}),
                }}
              />
            ))}
          </div>
        </>
      )}

      <FilterBar
        filterType={filterType}
        isLoadingFilteredPosts={isLoadingFilteredPosts}
        bargain_user_info={bargain_user_info}
        followed_chains={followed_chains}
        selectedChain={selectedChain}
        currentPage="posts"
        onFilterTypeChange={handleFilterTypeChange}
        onChainFilterChange={handleChainFilterChange}
      />

      {/* チェーン絞り込み時のヘッダー */}
      {filterType.startsWith('chain_') &&
        selectedChain &&
        (() => {
          const chain = followed_chains.find((c) => String(c.id) === selectedChain)
          if (!chain) return null
          return (
            <div style={styles.chainHeader}>
              {chain.logo_url && (
                <div style={styles.chainLogoContainer}>
                  <img src={chain.logo_url} alt={chain.name} style={styles.chainLogo} />
                </div>
              )}
              <h2 style={styles.chainTitle}>{chain.name}の口コミ一覧</h2>
            </div>
          )
        })()}

      <div
        style={
          filterType.startsWith('chain_') && selectedChain
            ? styles.postList
            : styles.postListWithPadding
        }
      >
        {isLoadingFilteredPosts ? (
          <div style={styles.filterLoadingOverlay}>
            <div style={styles.filterLoadingSpinner}>
              <div>絞り込み中...</div>
              <div style={{ marginTop: '8px', fontSize: '12px' }}>
                {filterType === 'following' && 'フォロー中のチェーンの投稿を絞り込んでいます'}
                {filterType === 'want_to_eat' && '「食べたい！」した投稿を絞り込んでいます'}
                {filterType.startsWith('chain_') &&
                  `${followed_chains.find((c) => String(c.id) === selectedChain)?.name}の投稿を絞り込んでいます`}
              </div>
            </div>
          </div>
        ) : (
          <InfinitePostList
            initialPosts={filteredPosts}
            bargain_user_info={bargain_user_info}
            fetchMorePosts={fetchMorePosts}
            emptyMessage={
              filterType === 'following'
                ? 'フォロー中のチェーンではまだ投稿はありません。'
                : filterType === 'want_to_eat'
                  ? '「食べたい！」を押した投稿はまだありません。'
                  : filterType.startsWith('chain_')
                    ? `${followed_chains.find((c) => String(c.id) === selectedChain)?.name}の投稿はまだありません。`
                    : 'まだ投稿はありません。'
            }
          />
        )}
      </div>

      <div style={styles.footerButtons}>
        <div style={{ flex: '0 0 auto', marginRight: '8px' }}>
          <BackButton
            href={
              bargain_user_info.type !== 'guest' ? '/webview/message_board/posts/new' : undefined
            }
            variant="primary"
            onClick={bargain_user_info.type === 'guest' ? () => setShowLoginModal(true) : undefined}
          >
            <span className="icon_font icf_camera" style={{ fontSize: '18px' }} />
            投稿する
          </BackButton>
        </div>
      </div>

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </PageContainer>
  )
}

export default Posts
